import { Link } from '@tanstack/react-router';
import { Fragment, useId, useState } from 'react';

import { Pagination } from '~/components/common/pagination';
import { SearchInput } from '~/components/common/search-input';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useDebouncedValue } from '~/hooks/use-debounced-value';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { useProductAttempts } from '../../hooks/use-batch-retry';
import {
  isPostStepLive,
  JOURNEY_PRODUCTS_PAGE_SIZE,
  useInvalidateOnPostStepTransition,
  useJourneyRunProducts,
  useJourneyRuns,
  usePostStepAttempts,
  useRedeliverPostStepRetry,
  useUndoPostStepAcceptance,
} from '../../hooks/use-post-step-retry';
import type { BatchStatus } from '../../types/batch-retry.api.types';
import {
  type JourneyClass,
  type JourneyProduct,
  type JourneyRun,
  type PostStepName,
  type PostStepScope,
  type PostSteps,
} from '../../types/post-step-retry.api.types';

import { AttemptsToggle, OutcomePill } from './batch-retry-shared';
import { formatCount, humanize, pageRange } from './batch-retry-utils';
import { PostStepAcceptDialog, type PostStepAcceptTarget } from './post-step-accept-dialog';
import {
  beforeLabel,
  CLASS_TONE,
  classesFor,
  attentionOf,
  healthOf,
  isAcceptable,
  maxAttemptsOf,
  redeliveryLabel,
  STEP_NAME,
  toFixOf,
  type StepKey,
  undeliveredReason,
} from './post-step-journey-utils';

export type JourneyStepKey = Exclude<StepKey, 'ENRICHMENT'>;

export interface PostStepJourneyProps {
  jobId: string;
  step: JourneyStepKey;
  postSteps: PostSteps;
  /** Products enriched so far (the SD / PD tiles read against it). */
  enrichedCount: number;
  /** The batch's delivery has gone out (otherwise Delivery shows one message). */
  delivered: boolean;
  /** Tells an undelivered batch's reason apart (approval gate vs failed vs still running). */
  batchStatus?: BatchStatus | null;
  /** Redeliver exists for GMC batches only. Null = the batch header has not loaded yet. */
  isGmc: boolean | null;
  /** Opens the retry dialog for this step. */
  onRetry: (step: PostStepName, scope?: PostStepScope) => void;
}

const RUN_STATUS_TONE: Record<string, string> = {
  COMPLETED: 'bg-emerald-50 text-emerald-700',
  COMPLETED_WITH_FAILURES: 'bg-warning-50 text-warning',
  RUNNING: 'bg-info-50 text-info',
  PENDING: 'bg-muted text-muted-foreground',
  FAILED: 'bg-error-50 text-error',
  CANCELLED: 'bg-muted text-muted-foreground',
  AWAITING_APPROVAL: 'bg-warning-50 text-warning',
  IN_PROGRESS: 'bg-info-50 text-info',
};

const RUN_DOT: Record<JourneyRun['kind'], string> = {
  ENRICHMENT: 'bg-warning',
  ENRICHMENT_RETRY: 'bg-emerald-500',
  POST_STEP_RETRY: 'bg-primary',
  BATCH_DELIVERY: 'bg-emerald-500',
};

const DELIVERED_CHIP = 'bg-emerald-50 text-emerald-700';

function isFinished(run: JourneyRun): boolean {
  return run.status === 'COMPLETED' || run.status === 'COMPLETED_WITH_FAILURES';
}

/** A redelivery that ended without sending; its fixes may be sent again. */
const REDELIVERY_RETRYABLE = new Set(['FAILED', 'TIMED_OUT', 'CANCELLED']);

/**
 * The Redeliver action's rules (contract §4): GMC, finished, something to send, and never sent
 * (or the last redelivery failed).
 */
function canRedeliver(run: JourneyRun, isGmc: boolean | null): boolean {
  const status = run.redelivery?.status ?? null;
  return (
    isGmc === true &&
    run.kind === 'POST_STEP_RETRY' &&
    isFinished(run) &&
    run.ready > 0 &&
    (status === null || REDELIVERY_RETRYABLE.has(status))
  );
}

/**
 * One post-step of the product journey (search descriptors, phrase descriptions or delivery):
 * four tiles, the runs that touched it (oldest first), and the selected run's products.
 */
export function PostStepJourney({
  jobId,
  step,
  postSteps,
  enrichedCount,
  delivered,
  batchStatus,
  isGmc,
  onRetry,
}: PostStepJourneyProps) {
  const waiting = step === 'DELIVERY' && !delivered;
  const { data: runs, isLoading, isError } = useJourneyRuns(jobId, step, !waiting);
  useInvalidateOnPostStepTransition(jobId, step, runs);
  const [picked, setPicked] = useState<{ step: string; runId: string } | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<PostStepAcceptTarget | null>(null);

  if (waiting) {
    const reason = undeliveredReason(batchStatus);
    const box = 'text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm';
    if (reason === 'FAILED') {
      return (
        <p data-testid="journey-delivery-failed" className={box}>
          <strong className="text-error">
            Delivery failed — nothing from this batch is confirmed delivered.
          </strong>
          <br />
          Retry the delivery, or open the delivery run to see why it failed.
        </p>
      );
    }
    if (reason === 'RETRYING' || reason === 'PAUSED') {
      return (
        <p data-testid="journey-delivery-on-hold" className={box}>
          <strong className="text-foreground">
            {reason === 'RETRYING' ? 'A retry is running for this batch.' : 'This batch is paused.'}
          </strong>
          <br />
          Its delivery shows here once the batch finishes.
        </p>
      );
    }
    if (reason === 'PENDING') {
      return (
        <p data-testid="journey-not-delivered" className={box}>
          <strong className="text-foreground">Not delivered yet.</strong>
          <br />
          Delivery has not gone out for this batch.
        </p>
      );
    }
    return (
      <p data-testid="journey-awaiting-approval" className={box}>
        <strong className="text-foreground">
          Not delivered yet — the batch is awaiting approval.
        </strong>
        <br />
        Everything fixed in the earlier steps is included automatically when the batch is approved.
        No redelivery needed.
      </p>
    );
  }
  if (isLoading) return <Skeleton data-testid="journey-loading" className="h-64 w-full" />;
  if (isError || !runs) {
    return (
      <p data-testid="journey-error" className="text-error text-sm">
        Could not load the runs for this step.
      </p>
    );
  }

  const live = isPostStepLive(runs) || postSteps.lastRetry?.status === 'RUNNING';
  const selected =
    (picked?.step === step ? runs.find((r) => r.runId === picked.runId) : undefined) ??
    runs[runs.length - 1];

  return (
    <div className="space-y-4">
      <JourneyTiles step={step} postSteps={postSteps} enrichedCount={enrichedCount} runs={runs} />
      {step === 'DELIVERY' && isGmc !== null && (
        <p data-testid="journey-delivery-intro" className="text-muted-foreground text-sm">
          {isGmc
            ? 'Run 1 is the batch delivery. Redelivery sends to Google the products a search or phrase retry fixed after it. Each finished retry gets its own Redeliver. Enrichment retries are not listed here.'
            : 'Redelivery is available for Google Merchant batches only, so the retries below are listed for reference.'}
        </p>
      )}
      {step !== 'DELIVERY' && (
        <SafeguardStrip
          step={step}
          postSteps={postSteps}
          live={live}
          onRetry={onRetry}
          onAccept={setAcceptTarget}
        />
      )}
      {runs.length === 0 ? (
        <p data-testid="journey-no-runs" className="text-muted-foreground text-sm">
          {step === 'DELIVERY'
            ? 'No retries have fixed products since delivery, so nothing needs redelivering.'
            : 'No runs have touched this step yet.'}
        </p>
      ) : (
        // Runs : products = 1 : 3, like the Enrichment step.
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]">
          <ol data-testid="journey-timeline" aria-label="Runs for this step, oldest first">
            {runs.map((run, index) => (
              <JourneyRunCard
                key={run.runId}
                step={step}
                run={run}
                isLast={index === runs.length - 1}
                selected={selected?.runId === run.runId}
                onSelect={() => {
                  setPicked({ step, runId: run.runId });
                }}
              />
            ))}
          </ol>
          {selected && (
            <JourneyPanel
              key={`${step}:${selected.runId}`}
              jobId={jobId}
              step={step}
              run={selected}
              postSteps={postSteps}
              live={live}
              isGmc={isGmc}
              onRetry={onRetry}
              onAccept={setAcceptTarget}
            />
          )}
        </div>
      )}
      <PostStepAcceptDialog
        jobId={jobId}
        target={acceptTarget}
        onClose={() => {
          setAcceptTarget(null);
        }}
      />
    </div>
  );
}

function JourneyTiles({
  step,
  postSteps,
  enrichedCount,
  runs,
}: {
  step: JourneyStepKey;
  postSteps: PostSteps;
  enrichedCount: number;
  runs: JourneyRun[];
}) {
  let tiles: [string, string, boolean?][];
  if (step === 'DELIVERY') {
    const batch = runs.find((r) => r.kind === 'BATCH_DELIVERY');
    const retries = runs.filter((r) => r.kind === 'POST_STEP_RETRY');
    const redelivered = retries.reduce((sum, r) => sum + (r.redelivery?.delivered ?? 0), 0);
    tiles = [
      ['Delivered in batch', batch?.delivery ? formatCount(batch.delivery.delivered ?? 0) : '—'],
      ['Fixed after delivery', formatCount(retries.reduce((sum, r) => sum + r.ready, 0))],
      ['Redelivered', formatCount(redelivered)],
      ['Not redelivered yet', formatCount(retries.filter((r) => !r.redelivery).length)],
    ];
  } else {
    const health = healthOf(postSteps, step);
    const toFix = toFixOf(step, health);
    tiles = [
      ['Enriched products', formatCount(enrichedCount)],
      [`${classesFor(step)[0]?.label ?? 'Ready'} now`, formatCount(health?.completed)],
      ['Still to fix', formatCount(toFix), toFix > 0],
      ['Fixed by retries', formatCount(health?.fixedByRetries ?? 0)],
    ];
  }
  return (
    <dl data-testid="journey-tiles" className="grid grid-cols-2 gap-2 lg:grid-cols-4">
      {tiles.map(([label, value, alert]) => (
        <div key={label} className="rounded-lg border px-3 py-2">
          <dt className="text-muted-foreground text-xs">{label}</dt>
          <dd
            data-testid={`journey-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={cn('text-lg font-semibold tabular-nums', alert && 'text-error')}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What a default retry will not pick, and the way forward for each: products that failed the
 * attempt limit (one more try, or accept as is), search descriptors with no keywords (add keywords
 * first), phrase descriptions waiting on search descriptors that cannot run, and accepted ones.
 */
function SafeguardStrip({
  step,
  postSteps,
  live,
  onRetry,
  onAccept,
}: {
  step: PostStepName;
  postSteps: PostSteps;
  live: boolean;
  onRetry: (step: PostStepName, scope?: PostStepScope) => void;
  onAccept: (target: PostStepAcceptTarget) => void;
}) {
  const { gaveUp, accepted, needsKeywords, waiting } = attentionOf(step, healthOf(postSteps, step));
  const cap = maxAttemptsOf(postSteps);
  if (gaveUp + accepted + needsKeywords + waiting === 0) return null;
  const many = (n: number, one: string, other: string) => (n === 1 ? one : other);
  return (
    <div
      data-testid="journey-safeguards"
      className="bg-warning-50/50 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2"
    >
      <ul className="space-y-0.5 text-sm">
        {gaveUp > 0 && (
          <li data-testid="journey-gave-up">
            <strong className="tabular-nums">{formatCount(gaveUp)}</strong>{' '}
            {many(gaveUp, 'product', 'products')} still failed after {String(cap)} retries and{' '}
            {many(gaveUp, 'is', 'are')} no longer retried automatically.
          </li>
        )}
        {needsKeywords > 0 && (
          <li data-testid="journey-needs-keywords">
            <strong className="tabular-nums">{formatCount(needsKeywords)}</strong> have no keywords.
            Add keywords for them, then retry &ldquo;No keywords&rdquo;.
          </li>
        )}
        {waiting > 0 && (
          <li data-testid="journey-waiting">
            <strong className="tabular-nums">{formatCount(waiting)}</strong> wait on search
            descriptors that gave up, have no keywords or were accepted as is.
          </li>
        )}
        {accepted > 0 && (
          <li data-testid="journey-accepted" className="text-muted-foreground">
            {formatCount(accepted)} accepted as is.
          </li>
        )}
      </ul>
      {gaveUp > 0 && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid="journey-retry-gave-up"
            className="cursor-pointer"
            disabled={live}
            onClick={() => {
              onRetry(step, 'GAVE_UP');
            }}
          >
            Try once more
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="journey-accept-gave-up"
            className="cursor-pointer"
            disabled={live}
            onClick={() => {
              onAccept({ step, gaveUpOnly: true, count: gaveUp });
            }}
          >
            Accept as is
          </Button>
        </div>
      )}
    </div>
  );
}

function JourneyRunCard({
  step,
  run,
  isLast,
  selected,
  onSelect,
}: {
  step: JourneyStepKey;
  run: JourneyRun;
  isLast: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const batch = run.kind === 'BATCH_DELIVERY';
  // The batch delivery reads like any run (status chip); a redelivery reads its own send state.
  const isDelivery = step === 'DELIVERY' && !batch;
  const okLabel = batch
    ? 'Delivered'
    : isDelivery
      ? 'Redelivered'
      : (classesFor(step)[0]?.label ?? 'Ready');
  const total = batch ? (run.delivery?.requested ?? run.total) : run.total;
  const ok = batch
    ? (run.delivery?.delivered ?? 0)
    : isDelivery
      ? (run.redelivery?.delivered ?? 0)
      : run.ready;
  const bad = batch
    ? (run.delivery?.failed ?? 0)
    : isDelivery
      ? (run.redelivery?.failed ?? 0)
      : run.toFix;
  const chip = isDelivery ? redeliveryLabel(run) : humanize(run.status ?? 'RUNNING');
  const chipTone = isDelivery
    ? chip === 'Redelivered'
      ? DELIVERED_CHIP
      : 'bg-info-50 text-info'
    : (RUN_STATUS_TONE[run.status ?? 'RUNNING'] ?? 'bg-muted text-muted-foreground');
  const pct = (n: number) => (total > 0 ? Math.round((n * 100) / total) : 0);

  return (
    <li
      data-testid={`journey-run-${run.runId}`}
      aria-current={selected ? 'true' : undefined}
      className="grid grid-cols-[20px_minmax(0,1fr)] gap-3"
    >
      <div className="relative flex justify-center">
        <span
          className={cn('z-10 mt-1 size-3 rounded-full ring-4 ring-white', RUN_DOT[run.kind])}
        />
        {!isLast && <span className="bg-border absolute top-5 -bottom-1 w-0.5" />}
      </div>
      <div
        className={cn(
          'relative mb-3 flex min-w-0 flex-col gap-1.5 rounded-lg border p-3 transition-colors',
          selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50 border-transparent',
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            title={run.runId}
            data-testid={`journey-run-select-${run.runId}`}
            aria-pressed={selected}
            onClick={onSelect}
            // Stretched over the card: clicking anywhere on it shows this run's products.
            className="cursor-pointer text-left text-sm font-semibold after:absolute after:inset-0 after:rounded-lg"
          >
            {isDelivery ? 'Redelivery · ' : ''}Run {run.seq} · {run.title}
          </button>
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', chipTone)}>
            {chip}
          </span>
        </div>
        {run.startedAt && (
          <p className="text-muted-foreground text-xs">{formatDateTime(run.startedAt)}</p>
        )}
        <div
          role="img"
          aria-label={`${formatCount(ok)} ${okLabel.toLowerCase()}, ${formatCount(bad)} to fix of ${formatCount(total)}`}
          className="bg-muted flex h-2 overflow-hidden rounded-full"
        >
          <span className="h-2 bg-emerald-500" style={{ width: `${String(pct(ok))}%` }} />
          <span className="bg-error h-2" style={{ width: `${String(pct(bad))}%` }} />
        </div>
        <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span>
            {isDelivery ? 'To send' : batch ? 'Sent' : 'Products'}{' '}
            <b className="text-foreground">{formatCount(total)}</b>
          </span>
          <span>
            {okLabel} <b className="text-foreground">{formatCount(ok)}</b>
          </span>
          <span>
            {isDelivery || batch ? 'Failed' : 'To fix'}{' '}
            <b className="text-foreground">{formatCount(bad)}</b>
          </span>
        </div>
      </div>
    </li>
  );
}

function JourneyPanel({
  jobId,
  step,
  run,
  postSteps,
  live,
  isGmc,
  onRetry,
  onAccept,
}: {
  jobId: string;
  step: JourneyStepKey;
  run: JourneyRun;
  postSteps: PostSteps;
  live: boolean;
  isGmc: boolean | null;
  onRetry: (step: PostStepName, scope?: PostStepScope) => void;
  onAccept: (target: PostStepAcceptTarget) => void;
}) {
  const classes = classesFor(step);
  const [cls, setCls] = useState<JourneyClass | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchId = useId();
  const q = useDebouncedValue(search.trim(), 300);
  const redeliver = useRedeliverPostStepRetry(jobId);
  const { data, isLoading, isError, isPlaceholderData } = useJourneyRunProducts(
    jobId,
    run.runId,
    step,
    {
      ...(cls === 'ALL' ? {} : { class: cls }),
      ...(q ? { q } : {}),
      page,
      size: JOURNEY_PRODUCTS_PAGE_SIZE,
    },
    run.status === 'RUNNING' || run.redelivery?.status === 'RUNNING',
  );
  const counts = data?.counts ?? run.counts;
  const pills = [
    { key: 'ALL' as const, label: 'All', count: run.total },
    ...classes
      .filter((c) => (counts[c.key] ?? 0) > 0 || c.key === cls)
      .map((c) => ({ key: c.key, label: c.label, count: counts[c.key] ?? 0 })),
  ];
  const activeDef = classes.find((c) => c.key === cls);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  const isDelivery = step === 'DELIVERY';
  const retryStep: PostStepName | null = isDelivery ? null : step;
  const toFix = retryStep ? toFixOf(retryStep, healthOf(postSteps, retryStep)) : 0;
  const redeliverable = canRedeliver(run, isGmc);
  const redeliveryBusy = run.redelivery?.status === 'RUNNING';

  return (
    <section
      data-testid="journey-panel"
      data-run-id={run.runId}
      className="min-w-0 space-y-4 rounded-lg border p-4"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h3 data-testid="journey-panel-title" className="text-sm font-semibold">
            Run {run.seq} · {STEP_NAME[step]}
          </h3>
          <p className="text-muted-foreground text-xs">{panelSubtitle(run, isDelivery)}</p>
        </div>
        {retryStep && (toFix > 0 || live) && (
          <Button
            size="sm"
            data-testid="journey-retry"
            className="cursor-pointer"
            disabled={live}
            onClick={() => {
              onRetry(retryStep);
            }}
          >
            {live
              ? 'Retry running…'
              : `Retry ${formatCount(toFix)} ${STEP_NAME[retryStep].toLowerCase()}`}
          </Button>
        )}
        {isDelivery && run.kind === 'POST_STEP_RETRY' && (redeliverable || run.redelivery) && (
          <Button
            size="sm"
            data-testid="journey-redeliver"
            className="cursor-pointer"
            isLoading={redeliver.isPending}
            disabled={!redeliverable || redeliver.isPending || redeliveryBusy}
            onClick={() => {
              redeliver.mutate(run.runId);
            }}
          >
            {!redeliverable
              ? redeliveryLabel(run)
              : run.redelivery
                ? `Redeliver ${formatCount(run.ready)} again`
                : `Redeliver ${formatCount(run.ready)} to Google`}
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={cls}
          onValueChange={(v) => {
            setCls(v as JourneyClass | 'ALL');
            setPage(0);
            setExpanded(null);
          }}
          className="min-w-fit flex-1"
        >
          <TabsList
            aria-label="Filter products"
            data-testid="journey-filter"
            className="w-full justify-evenly gap-1"
          >
            {pills.map((p) => (
              <TabsTrigger
                key={p.key}
                value={p.key}
                data-testid={`journey-filter-${p.key.toLowerCase()}`}
                className="w-full px-4"
              >
                {p.label}
                <span
                  data-testid={`journey-count-${p.key.toLowerCase()}`}
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                    cls === p.key ? 'text-primary bg-white' : 'bg-primary text-white',
                  )}
                >
                  {formatCount(p.count)}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <label htmlFor={searchId} className="sr-only">
          Search products
        </label>
        <SearchInput
          id={searchId}
          testId="journey-search"
          value={search}
          onChange={(next) => {
            setSearch(next);
            setPage(0);
            setExpanded(null);
          }}
          placeholder="Search by title or merchant id"
          className="w-full sm:w-80"
          inputClassName="h-9 text-sm"
        />
      </div>

      <p data-testid="journey-explain" className="text-muted-foreground text-sm">
        {activeDef
          ? `${activeDef.label} — ${activeDef.explain}`
          : run.status === 'RUNNING'
            ? 'Running now — counts update as products finish.'
            : 'Pick a filter to see what each group means and what to do.'}
      </p>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : isError || !data ? (
        <p data-testid="journey-products-error" className="text-error text-sm">
          Could not load this run&apos;s products.
        </p>
      ) : data.items.length === 0 ? (
        <p data-testid="journey-products-empty" className="text-muted-foreground text-sm">
          {q ? `No products match "${q}".` : 'Nothing here.'}
        </p>
      ) : (
        <Table data-testid="journey-table" containerClassName="max-h-[28rem] rounded-md border">
          <TableHeader className="bg-background sticky top-0 z-10 shadow-[0_1px_0_var(--border)]">
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Before this run</TableHead>
              <TableHead>This run</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Attempts</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((p) => {
              const def = classes.find((c) => c.key === p.class);
              return (
                <Fragment key={p.productId}>
                  <TableRow data-testid={`journey-row-${p.productId}`}>
                    <TableCell className="max-w-80">
                      <Link
                        to="/catalog-operations/$batchId/products/$productId"
                        params={{ batchId: jobId, productId: p.productId }}
                        className="text-primary block truncate text-sm hover:underline"
                        title={p.title ?? p.productId}
                      >
                        {p.title ?? p.merchantId ?? p.productId}
                      </Link>
                      <span
                        className="text-muted-foreground block truncate font-mono text-xs"
                        title={p.merchantId ?? p.productId}
                      >
                        {p.merchantId ?? p.productId}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {beforeLabel(step, run, p.before)}
                      {/* On Delivery the reason is what was wrong when the batch sent it. */}
                      {isDelivery && p.reason && (
                        <p
                          data-testid={`journey-before-reason-${p.productId}`}
                          className="max-w-xs truncate"
                          title={p.reason}
                        >
                          {p.reason}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <span
                          data-testid={`journey-class-${p.productId}`}
                          className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                            CLASS_TONE[def?.tone ?? 'info'],
                          )}
                        >
                          {def?.label ?? humanize(p.class)}
                        </span>
                        <SafeguardBadge product={p} cap={maxAttemptsOf(postSteps)} />
                        {p.reason && def?.tone !== 'ok' && !isDelivery && (
                          <p
                            className="text-muted-foreground max-w-xs truncate text-xs"
                            title={p.reason}
                          >
                            {p.reason}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {retryStep && (
                        <RowAcceptAction
                          jobId={jobId}
                          step={retryStep}
                          product={p}
                          // Rows of the previous filter / page are still shown while the new
                          // ones load: no action on them.
                          disabled={isPlaceholderData || live}
                          onAccept={onAccept}
                        />
                      )}
                      <AttemptsToggle
                        productId={p.productId}
                        expanded={expanded === p.productId}
                        onToggle={() => {
                          setExpanded((cur) => (cur === p.productId ? null : p.productId));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  {expanded === p.productId && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <JourneyAttempts jobId={jobId} productId={p.productId} step={step} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span data-testid="journey-range" className="text-muted-foreground text-xs tabular-nums">
            {pageRange(page, data.size, data.total)}
          </span>
          <Pagination
            currentPage={page + 1}
            totalPages={totalPages}
            testId="journey-pagination"
            onPageChange={(next) => {
              setPage(next - 1);
              setExpanded(null);
            }}
          />
        </div>
      )}
    </section>
  );
}

/** "Accepted" wins over "Gave up"; both read the live state, not the run's class. */
function SafeguardBadge({ product, cap }: { product: JourneyProduct; cap: number }) {
  if (product.accepted) {
    return (
      <span
        data-testid={`journey-accepted-${product.productId}`}
        className="bg-muted text-muted-foreground ml-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
      >
        Accepted as is
      </span>
    );
  }
  // Only while still unhealthy: a product fixed after its tries is no longer "gave up".
  if (isAcceptable(product.liveClass) && (product.attempts ?? 0) >= cap) {
    return (
      <span
        data-testid={`journey-gave-up-${product.productId}`}
        title={`Failed ${String(product.attempts)} retries; no longer retried automatically`}
        className="bg-warning-50 text-warning ml-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
      >
        Gave up · {String(product.attempts)} tries
      </span>
    );
  }
  return null;
}

/**
 * Accept one product as is, or undo it. Keyed on the live class (the row's class is how the run
 * left it); only on backends that report `liveClass` and `accepted`.
 */
function RowAcceptAction({
  jobId,
  step,
  product,
  disabled,
  onAccept,
}: {
  jobId: string;
  step: PostStepName;
  product: JourneyProduct;
  disabled: boolean;
  onAccept: (target: PostStepAcceptTarget) => void;
}) {
  const undo = useUndoPostStepAcceptance(jobId);
  if (product.accepted === undefined || product.accepted === null || !product.liveClass) {
    return null;
  }
  const name = product.title ?? product.merchantId ?? product.productId;
  if (product.accepted) {
    return (
      <Button
        size="sm"
        variant="ghost"
        data-testid={`journey-undo-accept-${product.productId}`}
        aria-label={`Undo accept for ${name}`}
        className="mr-1 cursor-pointer"
        isLoading={undo.isPending}
        disabled={disabled || undo.isPending}
        onClick={() => {
          undo.mutate({ step, productIds: [product.productId] });
        }}
      >
        Undo accept
      </Button>
    );
  }
  if (!isAcceptable(product.liveClass)) return null;
  return (
    <Button
      size="sm"
      variant="ghost"
      data-testid={`journey-accept-${product.productId}`}
      aria-label={`Accept ${name} as is`}
      className="mr-1 cursor-pointer"
      disabled={disabled}
      onClick={() => {
        onAccept({ step, productIds: [product.productId], count: 1 });
      }}
    >
      Accept as is
    </Button>
  );
}

function panelSubtitle(run: JourneyRun, isDelivery: boolean): string {
  if (run.kind === 'BATCH_DELIVERY') {
    return `Every product the batch sent to Google${run.startedAt ? ` · ${formatDateTime(run.startedAt)}` : ''}`;
  }
  if (isDelivery) {
    return 'Pushes the products this retry fixed · the batch delivery numbers do not change';
  }
  if (run.kind === 'POST_STEP_RETRY') {
    return `Retried only this step for products that were not ready${run.startedAt ? ` · ${formatDateTime(run.startedAt)}` : ''}`;
  }
  return run.kind === 'ENRICHMENT'
    ? 'How the original run left each product'
    : 'Products this retry enriched, and how they did in this step';
}

/** Every attempt at one product in this step: enrichment attempts first, then post-step retries. */
function JourneyAttempts({
  jobId,
  productId,
  step,
}: {
  jobId: string;
  productId: string;
  step: JourneyStepKey;
}) {
  const enrichment = useProductAttempts(jobId, productId);
  const postStep = usePostStepAttempts(jobId, productId);
  const loading = enrichment.isLoading || postStep.isLoading;
  const failed = enrichment.isError || postStep.isError;
  const retries = (postStep.data ?? []).filter((a) => step === 'DELIVERY' || a.step === step);
  return (
    <div
      id={`attempts-${productId}`}
      data-testid={`attempts-panel-${productId}`}
      className="bg-muted/40 space-y-2 rounded-md border p-3"
    >
      <p className="text-xs font-semibold">
        Every attempt · <span className="font-mono font-normal">{productId}</span>
      </p>
      {loading ? (
        <Skeleton className="h-10 w-full" />
      ) : failed ? (
        <p className="text-error text-xs">Could not load the attempt history.</p>
      ) : (
        <ol className="space-y-1.5">
          {(enrichment.data?.attempts ?? []).map((a) => (
            <li
              key={`e-${String(a.attempt)}-${a.runId ?? ''}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
            >
              <span className="font-medium">
                {a.attempt === 0 ? 'Original run' : `Retry #${String(a.attempt)}`} · enrichment
              </span>
              <OutcomePill outcome={a.outcome} />
              {a.at && (
                <span className="text-muted-foreground ml-auto">{formatDateTime(a.at)}</span>
              )}
            </li>
          ))}
          {retries.map((a) => {
            const at = a.completedAt ?? a.createdAt;
            return (
              <li
                key={`p-${a.retryId}-${a.step}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
              >
                <span className="font-medium">
                  {STEP_NAME[a.step]} retry
                  {a.priorClass ? ` · was ${humanize(a.priorClass).toLowerCase()}` : ''}
                </span>
                <span className="bg-muted rounded-full px-2 py-0.5 font-medium">
                  {OUTCOME_LABEL[a.outcome]}
                </span>
                {a.error && (
                  <span className="text-muted-foreground max-w-full truncate" title={a.error}>
                    {a.error}
                  </span>
                )}
                {at && <span className="text-muted-foreground ml-auto">{formatDateTime(at)}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Contract §3 outcome labels. */
const OUTCOME_LABEL: Record<string, string> = {
  PENDING: 'Queued',
  RECOVERED: 'Fixed',
  PARTIAL: 'Partly fixed (level 3 missing)',
  NO_COVERAGE: 'No keyword coverage',
  STILL_FAILED: 'Still failing',
  SKIPPED_SD_FAILED: 'Skipped — search descriptors failed',
  NOT_PERSISTED: 'Not saved — will be picked up by the next retry',
};
